/**
 * OcrQuality.js — Qualidade do OCR (Onda 15)
 * CI Sistemas de Informática Ltda. — FastALPR Parking
 *
 * Mostra cada leitura de placa com a leitura bruta do motor, a confiança de
 * cada caractere (colorida), a largura do bbox, o recorte da placa e o
 * snapshot, e permite ao operador registrar a placa correta. As correções
 * alimentam a taxa de acerto por câmera e período e o corpus de ajuste fino.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';
import '../styles/OcrQuality.css';

const PLATE_RE = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/;
const PERIOD_LABEL = { diurno: 'Diurno', crepusculo: 'Crepúsculo', noturno: 'Noturno' };
// Onda 58: por que uma leitura foi marcada para revisão. Hoje há um motivo; a
// tabela existe para o próximo não virar uma string solta na tela.
const REVIEW_LABEL = { placa_de_lado: 'placa de lado', placa_pequena_de_vies: 'placa pequena e de viés' };
// Onda 103: cada motivo tem a SUA explicação. A primeira regra exige leitura única; a
// segunda, não — dizer "única leitura da passagem" para as duas seria falso na segunda.
const REVIEW_DETALHE = {
  placa_de_lado: 'única leitura da passagem',
  placa_pequena_de_vies: 'câmera de entrada; nessa condição o OCR erra quase metade das vezes',
};
const REVIEW_DICA = {
  placa_de_lado: 'Registrada com uma única leitura, placa pequena e de lado. Vale conferir.',
  placa_pequena_de_vies: 'Câmera de entrada: placa pequena e de viés (o carro lido ainda atravessado). Nessa condição quase metade das leituras sai com um caractere trocado. Vale conferir.',
};

function fmtDateTime(dt) { return dt ? new Date(dt).toLocaleString('pt-BR') : '—'; }
function confClass(c) { if (c == null) return ''; if (c >= 0.95) return 'ok'; if (c >= 0.8) return 'warn'; return 'bad'; }
function fileUrl(relPath, token) { return relPath ? `/api/ocr-quality/file/${relPath}?token=${encodeURIComponent(token || '')}` : null; }

// ─── Placa com um caractere por célula, colorido pela confiança ──────────────
function PlateChars({ plate, confs, corrected }) {
  const chars = (plate || '').split('');
  return (
    <span className={`ocrq-plate ${corrected ? 'ocrq-plate-wrong' : ''}`}>
      {chars.map((ch, i) => (
        <span key={i} className={`ocrq-char ${confs ? confClass(confs[i]) : ''}`} title={confs ? `${(confs[i] * 100).toFixed(1)}%` : ''}>{ch}</span>
      ))}
    </span>
  );
}

// ─── Onda 177: o dono da placa (associado; morador e unidade; visitante) ──────
// Um associado lido com um caractere trocado aparece "sem cadastro" — salta aos olhos (FOF0512 → FQF0512).
const dataCurta = (v) => { const d = new Date(v); return Number.isNaN(d.getTime()) ? '?' : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }); };
const dataHoraCurta = (v) => { const d = new Date(v); return Number.isNaN(d.getTime()) ? '?' : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };

function OwnerCell({ owner }) {
  if (!owner) return <span className="ocrq-muted" title="Placa sem cadastro de associado, morador ou visitante">sem cadastro</span>;
  const local = [owner.bloco ? `Bloco ${owner.bloco}` : null, owner.unidade ? `Unid. ${owner.unidade}` : null].filter(Boolean).join(' · ');
  return (
    <span className="ocrq-owner">
      <span className="ocrq-owner-name" title={owner.nome}>{owner.nome || '—'}</span>
      {(local || owner.tipo === 'visitante' || !owner.ativo) && (
        <span className="ocrq-owner-sub">
          {local}
          {owner.tipo === 'visitante' && <span className="ocrq-badge muted" title={owner.anfitriao ? `Autorizado por ${owner.anfitriao}` : ''}>visitante{owner.anfitriao ? ` de ${owner.anfitriao}` : ''}</span>}
          {/* Onda 180c: a validade da autorização de visita mais recente — vencida não libera nada. */}
          {owner.tipo === 'visitante' && owner.autorizacao === 'vencida' && (
            <span className="ocrq-badge warn" title={`Última autorização de visita válida até ${dataHoraCurta(owner.autorizacao_ate)}: não libera a entrada nem a saída`}>autorização vencida ({dataCurta(owner.autorizacao_ate)})</span>
          )}
          {owner.tipo === 'visitante' && owner.autorizacao === 'futura' && (
            <span className="ocrq-badge warn" title={`Autorização de visita só a partir de ${dataHoraCurta(owner.autorizacao_de)}`}>autorização a partir de {dataCurta(owner.autorizacao_de)}</span>
          )}
          {owner.tipo === 'visitante' && owner.autorizacao === 'vigente' && (
            <span className="ocrq-badge ok" title={`Autorização de visita válida até ${dataHoraCurta(owner.autorizacao_ate)}`}>autorizado</span>
          )}
          {!owner.ativo && <span className="ocrq-badge warn">inativo</span>}
        </span>
      )}
    </span>
  );
}

// ─── Uso da leitura: o que ela produziu no pátio (Onda 32) ───────────────────
// Duas leituras da mesma passagem chegam iguais à tela; só uma registra o
// movimento. Sem este selo, não havia como saber qual delas valeu.
function UsageBadge({ usage }) {
  if (!usage) return <span className="ocrq-muted" title="Leitura anterior à Onda 32, sem vínculo gravado">—</span>;
  const texto = usage.ticket_id
    ? `${usage.label} #${usage.ticket_id}${usage.ticket_plate ? ` (${usage.ticket_plate})` : ''}`
    : usage.label;
  return <span className={`ocrq-badge ${usage.used ? 'ok' : 'muted'}`} title={texto}>{texto}</span>;
}

// ─── Modal de correção / inspeção ────────────────────────────────────────────
function ReadingModal({ reading, token, onClose, onSaved }) {
  // Onda 149: corrigir exige `ocr_correct` (operador para cima). O perfil Supervisão abre o
  // detalhe da leitura só para ver.
  const { can } = useAuth();
  const podeCorrigir = typeof can === 'function' ? can('ocr_correct') : true;
  const [plate, setPlate] = useState(reading.corrected_plate || reading.plate || '');
  const [saving, setSaving] = useState(false);
  const normalized = plate.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const valid = PLATE_RE.test(normalized);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      const resp = await api.post(`/api/ocr-quality/readings/${reading.id}/correct`, { plate: normalized });
      const t = resp.data.ticket;
      if (resp.data.confirmed) {
        toast.success(`Leitura ${normalized} confirmada`);
      } else if (t && t.alterado) {
        toast.success(`Correção registrada: ${reading.plate} → ${normalized} · ticket #${t.id} corrigido`);
      } else if (t && t.id) {
        // A leitura foi corrigida, mas o ticket não: o motivo vem do servidor.
        toast.success(`Correção registrada: ${reading.plate} → ${normalized}`);
        toast(`Ticket #${t.id} não alterado: ${t.motivo}`, { icon: 'ℹ️' });
      } else {
        toast.success(`Correção registrada: ${reading.plate} → ${normalized}`);
      }
      onSaved({ ...reading, corrected_plate: normalized, corrected_at: new Date().toISOString(), is_wrong: normalized !== reading.plate });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao registrar correção');
    } finally {
      setSaving(false);
    }
  };

  const crop = fileUrl(reading.plate_crop_path, token);
  const snap = fileUrl(reading.engine_snapshot_path, token);
  return (
    <div className="ocrq-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ocrq-modal">
        <div className="ocrq-modal-header">
          <div>
            <h3>Leitura #{reading.id} · {reading.camera_name || `Câmera ${reading.camera_id}`} · {reading.log_type === 'entrada' ? 'Entrada' : 'Saída'}</h3>
            <p>{fmtDateTime(reading.detected_at)} · {PERIOD_LABEL[reading.period] || reading.period}</p>
          </div>
          <button className="ocrq-close" onClick={onClose} aria-label="Fechar">×</button>
        </div>
        <div className="ocrq-modal-body">
          <div className="ocrq-modal-images">
            {snap ? <img src={snap} alt="Frame da leitura" className="ocrq-snapshot" /> : <div className="ocrq-noimg">Frame não disponível</div>}
            {crop ? <img src={crop} alt="Recorte da placa" className="ocrq-crop-large" /> : <div className="ocrq-noimg">Recorte não disponível (leitura anterior à Onda 11)</div>}
          </div>
          <div className="ocrq-modal-facts">
            <div className="ocrq-fact"><span>Placa registrada</span><PlateChars plate={reading.plate} confs={reading.char_confidences} /></div>
            <div className="ocrq-fact"><span>Leitura bruta do OCR</span><code>{reading.raw_plate || '—'}</code></div>
            <div className="ocrq-fact"><span>Confiança média · mínima</span><code>{Number(reading.confidence).toFixed(3)} · {reading.min_char_confidence != null ? reading.min_char_confidence.toFixed(3) : '—'}</code></div>
            <div className="ocrq-fact"><span>Bbox · detector</span><code>{reading.bbox_width ? `${reading.bbox_width} px` : '—'}{reading.bbox_height ? ` × ${reading.bbox_height}` : ''} · {reading.detector_confidence != null ? Number(reading.detector_confidence).toFixed(2) : '—'}</code></div>
            {reading.plate_aspect !== null && reading.plate_aspect !== undefined && (
              <div className="ocrq-fact">
                <span>Proporção · posição</span>
                <code title="Uma placa vista de frente tem proporção próxima de 3; quanto mais em ângulo, menor. A posição vai de 0 (borda esquerda do quadro) a 1 (direita).">
                  {reading.plate_aspect.toFixed(2)}
                  {reading.plate_position_x !== null && reading.plate_position_x !== undefined
                    ? ` · ${reading.plate_position_x.toFixed(2)}` : ''}
                </code>
              </div>
            )}
            {reading.review_reason && (
              <div className="ocrq-fact"><span>Marcada para revisão</span><code className="ocrq-warn-text">{REVIEW_LABEL[reading.review_reason] || reading.review_reason}{REVIEW_DETALHE[reading.review_reason] ? ` — ${REVIEW_DETALHE[reading.review_reason]}` : ''}</code></div>
            )}
            <div className="ocrq-fact"><span>Corrigida pelo validador</span><code>{reading.ocr_corrected ? 'sim' : 'não'}</code></div>
            {reading.corrected_plate && (
              <div className="ocrq-fact"><span>Revisão do operador</span><code>{reading.corrected_plate} · {reading.corrected_by || ''} · {fmtDateTime(reading.corrected_at)}</code></div>
            )}
            {podeCorrigir && (
              <>
                <label className="ocrq-correct-label">Placa correta
                  <input value={plate} onChange={e => setPlate(e.target.value.toUpperCase())} maxLength={8} className={`ocrq-input ${plate && !valid ? 'invalid' : ''}`} placeholder="ABC1D23" />
                </label>
                <p className="ocrq-hint">Confirme a placa lida ou digite a correta. A leitura do motor é preservada; a correção fica registrada com seu usuário e alimenta a taxa de acerto. Se esta leitura abriu um ticket, a placa do ticket é corrigida junto.</p>
              </>
            )}
            <div className="ocrq-modal-actions">
              <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Fechar</button>
              {podeCorrigir && (
                <button className="btn btn-primary" onClick={save} disabled={!valid || saving}>{saving ? 'Salvando…' : (normalized === reading.plate ? 'Confirmar leitura' : 'Registrar correção')}</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────
export default function OcrQuality() {
  const { token, socket, can, user } = useAuth();
  const podeCorrigirLista = typeof can === 'function' ? can('ocr_correct') : true; // Onda 149
  // Onda 161: a grade por câmera e o Painel do período são diagnóstico — só administradores (o
  // servidor nem os envia aos demais).
  const veDetalhe = user?.role === 'admin' || user?.role === 'superadmin';
  // Atualização automática (Onda 51b): ligada por padrão, lembrada no navegador.
  const [autoRefresh, setAutoRefresh] = useState(() => {
    try { return localStorage.getItem('fastalpr_ocrq_auto') !== '0'; } catch (_) { return true; }
  });
  const refreshTimer = useRef(null);
  // Onda 174: cada bloco acima da lista pode ser escondido, para a relação de placas subir na tela. Pedido do
  // Iatagã (17/10): só os cartões do topo à vista; a grade por câmera e o Painel do período escondidos por
  // padrão, a um clique em "Mostrar". A escolha fica lembrada no navegador.
  const [mostrar, setMostrar] = useState(() => {
    const padrao = { cartoes: true, grade: false, painel: false };
    try { return { ...padrao, ...JSON.parse(localStorage.getItem('fastalpr_ocrq_mostrar') || '{}') }; } catch (_) { return padrao; }
  });
  const alternarBloco = (bloco, ligado) => {
    setMostrar(prev => {
      const novo = { ...prev, [bloco]: ligado };
      try { localStorage.setItem('fastalpr_ocrq_mostrar', JSON.stringify(novo)); } catch (_) { /* sem armazenamento: vale só nesta visita */ }
      return novo;
    });
  };
  // O operador olha o dia corrente; 7 dias é consulta, não rotina.
  const [days, setDays] = useState(1);
  const [stats, setStats] = useState(null);
  const [readings, setReadings] = useState([]);
  const [comDono, setComDono] = useState(false); // Onda 177
  const [loading, setLoading] = useState(false);
  const [cameras, setCameras] = useState([]);
  // A tela abre mostrando só as leituras que tiveram efeito — as que abriram ou
  // fecharam um ticket. As descartadas pelo próprio sistema continuam a um
  // clique no seletor "Uso", mas não poluem a revisão do operador: uma leitura
  // que o sistema jogou fora pode estar errada sem que nada tenha saído errado.
  const [filters, setFilters] = useState({ camera_id: '', log_type: '', plate: '', period: '', usage: 'registro', only_weak: false, only_corrected: false, only_reviewed: false, only_review: false, limit: 50 });
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalExato, setTotalExato] = useState(true);

  const loadStats = useCallback(async () => {
    try { const r = await api.get(`/api/ocr-quality/stats?days=${days}`); setStats(r.data); } catch (_) { setStats(null); }
  }, [days]);

  // Onda 40: a paginação passou para o servidor. O estado guarda a página, e o
  // total vem do backend — antes a tela recebia até 500 linhas de uma vez e não
  // tinha como avançar para além delas.
  const loadReadings = useCallback(async (paginaAlvo = page, tamanho = filters.limit, dias = days) => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v !== false) p.append(k, String(v)); });
      // O tamanho vem por argumento porque `setFilters` é assíncrono: trocar o
      // seletor e buscar na mesma ação usaria o valor antigo do estado.
      p.set('limit', String(tamanho));
      p.set('offset', String((paginaAlvo - 1) * tamanho));
      // O período escolhido nos botões vale para a lista, não só para o painel.
      p.set('days', String(dias));
      const r = await api.get(`/api/ocr-quality/readings?${p.toString()}`);
      const dados = r.data || {};
      setReadings(dados.items || []);
      // Onda 177: a coluna do dono só onde há cadastro de pessoas (clube e condomínio; no estacionamento, não).
      setComDono(dados.vertical === 'clube_social' || dados.vertical === 'condominio');
      setTotal(Number(dados.total) || 0);
      setTotalExato(dados.exact !== false);
      setPage(paginaAlvo);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao carregar leituras');
    } finally { setLoading(false); }
  }, [filters, page, days]);

  useEffect(() => { api.get('/api/cameras').then(r => setCameras(r.data || [])).catch(() => setCameras([])); }, []);
  useEffect(() => { loadStats(); }, [loadStats]);
  // Só os filtros disparam recarga; trocar de página chama loadReadings direto.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadReadings(1); }, [filters]);

  // Recarrega painel e lista a cada 20 s, mantendo página e filtros — só com a
  // aba visível e sem leitura aberta, para não trocar a lista debaixo de uma
  // revisão em andamento. O relógio não depende de nada: o AuthProvider desta
  // versão não recebe socket (useAuth().socket é undefined), e a primeira
  // versão desta função, que esperava por ele, nunca chegava a rodar. Se um
  // dia houver socket, 'plate_detected' antecipa a recarga.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const recarregar = () => {
      if (document.hidden || selected) return;
      loadStats();
      loadReadings(page);
    };
    const antecipar = () => {
      if (document.hidden || selected) return;
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(recarregar, 2500);
    };
    const aoVoltar = () => { if (!document.hidden) recarregar(); };
    const relogio = setInterval(recarregar, 20000);
    document.addEventListener('visibilitychange', aoVoltar);
    if (socket && typeof socket.on === 'function') socket.on('plate_detected', antecipar);
    return () => {
      clearInterval(relogio);
      clearTimeout(refreshTimer.current);
      document.removeEventListener('visibilitychange', aoVoltar);
      if (socket && typeof socket.off === 'function') socket.off('plate_detected', antecipar);
    };
  }, [socket, autoRefresh, selected, page, loadStats, loadReadings]);

  const alternarAuto = (ligado) => {
    setAutoRefresh(ligado);
    try { localStorage.setItem('fastalpr_ocrq_auto', ligado ? '1' : '0'); } catch (_) { /* sem armazenamento, segue ligado só nesta visita */ }
  };

  const set = (k, v) => { setPage(1); setFilters(prev => ({ ...prev, [k]: v })); };
  const s = stats && stats.summary;

  return (
    <div className="ocrq-page">
      <div className="page-header">
        <div>
          <h1>Qualidade do OCR</h1>
          <p className="page-subtitle">Leitura bruta, confiança por caractere, recorte e correção pelo operador</p>
        </div>
        <div className="ocrq-days">
          {[1, 7, 14, 30].map(d => (
            <button key={d} className={`btn btn-sm ${days === d ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setDays(d); loadReadings(1, filters.limit, d); }}>
              {d} {d === 1 ? 'dia' : 'dias'}
            </button>
          ))}
        </div>
      </div>

      <div className="ocrq-mostrar" role="group" aria-label="Blocos visíveis acima da lista">
        <span className="ocrq-mostrar-titulo">Mostrar</span>
        {[
          ['cartoes', 'Resumo'],
          ...(veDetalhe ? [['grade', 'Por câmera e período'], ['painel', 'Painel do período']] : []),
        ].map(([bloco, rotulo]) => (
          <label key={bloco} className={`ocrq-chip ${mostrar[bloco] ? '' : 'is-off'}`}>
            <input type="checkbox" checked={mostrar[bloco]} onChange={e => alternarBloco(bloco, e.target.checked)} /> {rotulo}
          </label>
        ))}
      </div>

      {s && mostrar.cartoes && (
        <div className="ocrq-cards">
          <div className="ocrq-card"><span className="ocrq-card-label">Leituras</span><span className="ocrq-card-value">{s.total}</span><span className="ocrq-card-sub">{s.with_diag} com diagnóstico por caractere</span></div>
          <div className="ocrq-card"><span className="ocrq-card-label">Com caractere fraco</span><span className="ocrq-card-value">{s.weak_pct != null ? `${s.weak_pct}%` : '—'}</span><span className="ocrq-card-sub">{s.weak} leituras com algum caractere abaixo de 0,90</span></div>
          <div className="ocrq-card"><span className="ocrq-card-label">Corrigidas pelo validador</span><span className="ocrq-card-value">{s.validator_corrected}</span><span className="ocrq-card-sub">trocas posicionais aplicadas pelo motor</span></div>
          <div className="ocrq-card"><span className="ocrq-card-label">Acerto nas revisadas</span><span className="ocrq-card-value">{s.accuracy_pct != null ? `${s.accuracy_pct}%` : '—'}</span><span className="ocrq-card-sub" title="Mede apenas as leituras que abriram ou fecharam um ticket. Leituras que o sistema descartou aparecem à parte: elas não afetaram a operação.">{s.operator_reviewed} revisadas · {s.operator_wrong} erradas{s.discarded_wrong ? ` · ${s.discarded_wrong} errada${s.discarded_wrong > 1 ? 's' : ''} já descartada${s.discarded_wrong > 1 ? 's' : ''} pelo sistema` : ''}{s.passage_corrected ? ` · ${s.passage_corrected} corrigidas pela passagem` : ''}</span></div>
          <div className="ocrq-card"><span className="ocrq-card-label">Aguardando revisão</span><span className={`ocrq-card-value ${s.review_pending ? 'ocrq-warn-text' : ''}`}>{s.review_pending ?? '—'}</span><span className="ocrq-card-sub" title="Aberturas registradas com uma única leitura, placa pequena e de lado — o perfil do GIS5C12 de 09/09. Não é erro: é onde o erro se esconde quando acontece.">{s.review_flagged ?? 0} marcadas no período · placa pequena e de lado</span></div>
          <div className="ocrq-card"><span className="ocrq-card-label">Largura média da placa</span><span className="ocrq-card-value">{s.bbox_avg != null ? `${s.bbox_avg} px` : '—'}</span><span className="ocrq-card-sub">no frame processado (1920×1080 na entrada, 1280×720 na saída)</span></div>
        </div>
      )}

      {veDetalhe && mostrar.grade && stats && stats.groups && stats.groups.length > 0 && (
        <div className="ocrq-table-wrap">
          <table className="ocrq-table ocrq-stats">
            <thead><tr><th>Câmera</th><th>Período</th><th>Leituras</th><th>Caractere fraco</th><th>Validador</th><th>Revisadas</th><th>Erradas</th><th>Acerto</th><th>Conf. média</th><th>Bbox médio</th></tr></thead>
            <tbody>
              {stats.groups.map(g => (
                <tr key={`${g.camera_id}-${g.period}`}>
                  <td>{g.camera_name || `Câmera ${g.camera_id}`}</td><td>{PERIOD_LABEL[g.period]}</td><td>{g.total}</td>
                  <td>{g.weak_pct != null ? `${g.weak_pct}%` : '—'}</td><td>{g.validator_corrected}</td><td>{g.operator_corrected}</td><td>{g.operator_wrong}</td>
                  <td>{g.accuracy_pct != null ? `${g.accuracy_pct}%` : '—'}</td><td>{g.conf_avg != null ? g.conf_avg.toFixed(3) : '—'}</td><td>{g.bbox_avg != null ? `${g.bbox_avg} px` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {veDetalhe && mostrar.painel && stats && stats.daily && stats.daily.length > 0 && (
        <section className="ocrq-panel">
          <h2>Painel do período <span className="ocrq-muted">({stats.days} dias)</span></h2>
          <div className="ocrq-panel-grid">
            <div className="ocrq-panel-block">
              <h3>Por dia</h3>
              <div className="ocrq-table-wrap">
                <table className="ocrq-table">
                  <thead><tr><th>Dia</th><th>Leituras</th><th>Caractere fraco</th><th>Revisadas</th><th>Acerto</th><th>Placa (mediana)</th></tr></thead>
                  <tbody>
                    {stats.daily.map(d => {
                      const max = Math.max(...stats.daily.map(x => x.total)) || 1;
                      return (
                        <tr key={d.day}>
                          <td className="ocrq-nowrap">{d.day.slice(8, 10)}/{d.day.slice(5, 7)}</td>
                          <td>
                            {/* A barra vive numa trilha de largura fixa: a porcentagem resolve
                                contra a trilha, não contra a célula — que é o que fazia a barra
                                invadir a coluna vizinha. */}
                            <span className="ocrq-bar-cell">
                              <span className="ocrq-bar-track"><span className="ocrq-bar" style={{ width: `${Math.round((d.total / max) * 100)}%` }} /></span>
                              <span className="ocrq-bar-value">{d.total}</span>
                            </span>
                          </td>
                          <td className={d.weak_pct != null && d.weak_pct > 20 ? 'ocrq-warn-text' : ''}>{d.weak_pct != null ? `${d.weak_pct}%` : '—'}</td>
                          <td>{d.reviewed}{d.wrong ? <span className="ocrq-muted"> · {d.wrong} erradas</span> : null}</td>
                          <td>{d.accuracy_pct != null ? `${d.accuracy_pct}%` : '—'}</td>
                          <td>{d.bbox_median != null ? `${d.bbox_median} px` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="ocrq-panel-block">
              <h3>Largura da placa na imagem <span className="ocrq-muted">(a entrada do OCR tem 128 px)</span></h3>
              {(stats.bbox_hist || []).map(h => {
                const max = Math.max(...h.buckets.map(b => b.count)) || 1;
                return (
                  <div key={h.camera_id} className="ocrq-hist">
                    <div className="ocrq-hist-title">{h.camera_name || `Câmera ${h.camera_id}`} <span className="ocrq-muted">· mediana {h.median} px · {h.below_128_pct}% abaixo de 128 px</span></div>
                    {h.buckets.map(b => (
                      <div key={b.key} className="ocrq-hist-row">
                        <span className="ocrq-hist-label">{b.key}</span>
                        <span className="ocrq-bar ocrq-bar-hist" style={{ width: `${Math.round((b.count / max) * 100)}%` }} />
                        <span className="ocrq-hist-count">{b.count}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            <div className="ocrq-panel-block">
              <h3>Leituras descartadas pela zona <span className="ocrq-muted">(fotos “forazona”, uma por placa a cada 30 min)</span></h3>
              {(stats.discarded || []).length === 0 ? <div className="ocrq-muted">Nenhuma no período.</div> : (
                <table className="ocrq-table">
                  <thead><tr><th>Câmera</th><th>Placa</th><th>Fotos</th><th>Última</th></tr></thead>
                  <tbody>
                    {stats.discarded.map(d => (
                      <tr key={`${d.camera_id}-${d.plate}`}><td>{cameras.find(c => c.id === d.camera_id)?.name || `Câmera ${d.camera_id}`}</td><td className="ocrq-plate">{d.plate}</td><td>{d.count}</td><td className="ocrq-nowrap">{d.last}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="ocrq-muted ocrq-hint">Placa que se repete todo dia é um carro estacionado ao fundo ou texto fixo na imagem; placa que aparece uma vez pode ser um carro real fora da zona.</p>
            </div>
          </div>
        </section>
      )}

      <div className="ocrq-filters">
        <select value={filters.camera_id} onChange={e => set('camera_id', e.target.value)}>
          <option value="">Todas as câmeras</option>
          {cameras.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filters.log_type} onChange={e => set('log_type', e.target.value)}>
          <option value="">Entrada e saída</option><option value="entrada">Entrada</option><option value="saida">Saída</option>
        </select>
        <select value={filters.period} onChange={e => set('period', e.target.value)}>
          <option value="">Todos os períodos</option><option value="diurno">Diurno</option><option value="crepusculo">Crepúsculo</option><option value="noturno">Noturno</option>
        </select>
        <select value={filters.usage} onChange={e => set('usage', e.target.value)}>
          <option value="">Todas as leituras</option>
          {/* Onda 59: nas verticais sem ticket, "registrar" é registrar o acesso —
              não abrir ou fechar um ticket, que ali não existe. */}
          <option value="registro">Só as que registraram o movimento</option>
          <option value="sem_registro">Só as que não geraram registro</option>
          <option value="sem_vinculo">Sem vínculo gravado (antes da Onda 32)</option>
        </select>
        <input placeholder="Placa (final, bruta ou corrigida)" value={filters.plate} onChange={e => set('plate', e.target.value.toUpperCase())} />
        <label><input type="checkbox" checked={filters.only_weak} onChange={e => set('only_weak', e.target.checked)} /> Só com caractere fraco</label>
        <label><input type="checkbox" checked={filters.only_corrected} onChange={e => set('only_corrected', e.target.checked)} /> Só corrigidas pelo validador</label>
        <label><input type="checkbox" checked={filters.only_reviewed} onChange={e => set('only_reviewed', e.target.checked)} /> Só revisadas</label>
        <label title="Aberturas registradas com uma única leitura, placa pequena e de lado, que ninguém olhou ainda."><input type="checkbox" checked={filters.only_review} onChange={e => set('only_review', e.target.checked)} /> Só aguardando revisão</label>
        <select value={filters.limit} onChange={e => set('limit', Number(e.target.value))}>
          {[50, 100, 200, 500].map(n => <option key={n} value={n}>{n} linhas</option>)}
        </select>
        <button className="btn btn-secondary btn-sm" onClick={() => loadReadings(page)} disabled={loading}>{loading ? 'Carregando…' : 'Atualizar'}</button>
        <label title="Recarrega painel e lista a cada 20 segundos, mantendo página e filtros. Pausa enquanto uma leitura estiver aberta."><input type="checkbox" checked={autoRefresh} onChange={e => alternarAuto(e.target.checked)} /> Atualização automática</label>
      </div>

      <div className="ocrq-table-wrap">
        <table className="ocrq-table">
          <thead>
            {/* Onda 177: a bruta desce para baixo da placa (só quando difere) e Mín. + Bbox viram uma coluna, para
                caber a coluna do dono sem rolagem lateral. */}
            <tr><th>Hora</th><th>Câmera</th><th>Dir.</th><th>Recorte</th><th>Placa (confiança por caractere)</th>{comDono && <th>Pessoa / unidade</th>}<th title="Confiança do caractere mais fraco · largura da placa no quadro">Mín. · largura</th><th>Uso</th><th>Validador</th><th>Revisão</th><th></th></tr>
          </thead>
          <tbody>
            {readings.length === 0 && !loading && <tr><td colSpan={comDono ? 11 : 10} className="ocrq-empty">Nenhuma leitura para os filtros escolhidos.</td></tr>}
            {readings.map(r => (
              <tr key={r.id} className={[r.is_wrong ? 'ocrq-row-wrong' : '', r.usage && !r.usage.used ? 'ocrq-row-unused' : ''].filter(Boolean).join(' ')}>
                <td className="ocrq-nowrap">{fmtDateTime(r.detected_at)}</td>
                <td>{r.camera_name || `Câmera ${r.camera_id}`}</td>
                <td>{r.log_type === 'entrada' ? '⬆' : '⬇'}</td>
                <td>{r.plate_crop_path ? <img className="ocrq-crop-thumb" src={fileUrl(r.plate_crop_path, token)} alt="" loading="lazy" onClick={() => setSelected(r)} /> : <span className="ocrq-muted">—</span>}</td>
                <td>
                  <PlateChars plate={r.plate} confs={r.char_confidences} corrected={r.is_wrong} />
                  {r.raw_plate && r.raw_plate !== r.plate && <span className="ocrq-raw">bruta <code className="ocrq-diff">{r.raw_plate}</code></span>}
                </td>
                {comDono && <td><OwnerCell owner={r.owner} /></td>}
                <td className="ocrq-nowrap">
                  <span className={confClass(r.min_char_confidence)}>{r.min_char_confidence != null ? r.min_char_confidence.toFixed(2) : '—'}</span>
                  <span className="ocrq-muted"> · {r.bbox_width ? `${r.bbox_width} px` : '—'}</span>
                </td>
                <td><UsageBadge usage={r.usage} /></td>
                <td>{r.ocr_corrected ? '✔' : ''}</td>
                <td>
                  {r.corrected_plate && r.corrected_by !== 'passagem'
                    ? <span className={r.is_wrong ? 'ocrq-badge bad' : 'ocrq-badge ok'}>{r.is_wrong ? `→ ${r.corrected_plate}` : 'confirmada'}</span>
                    : r.review_reason
                      ? <span className="ocrq-badge warn" title={REVIEW_DICA[r.review_reason] || 'Marcada para revisão. Vale conferir.'}>revisar · {REVIEW_LABEL[r.review_reason] || r.review_reason}</span>
                      : r.corrected_plate
                        ? <span className={r.is_wrong ? 'ocrq-badge bad' : 'ocrq-badge ok'}>{r.is_wrong ? `→ ${r.corrected_plate}` : 'confirmada'}</span>
                        : ''}
                </td>
                <td><button className="btn btn-sm btn-secondary" onClick={() => setSelected(r)}>{podeCorrigirLista ? 'Revisar' : 'Ver'}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* A paginação é servida pelo backend desde a Onda 40. O controle passou a
          ser o mesmo da plataforma: antes eram só "Anterior" e "Próxima", sem
          dizer quantas leituras existiam nem permitir trocar o tamanho da
          página, e sumia quando cabia tudo numa página só. */}
      <Pagination
        currentPage={page}
        totalPages={Math.max(1, Math.ceil(total / filters.limit))}
        pageSize={filters.limit}
        totalRecords={total}
        onPageChange={(p) => loadReadings(p)}
        onPageSizeChange={(tam) => { setFilters(f => ({ ...f, limit: tam })); loadReadings(1, tam); }}
        approximate={!totalExato}
        itemLabel="leitura"
      />

      {selected && (
        <ReadingModal
          reading={selected}
          token={token}
          onClose={() => setSelected(null)}
          onSaved={updated => { setReadings(prev => prev.map(r => (r.id === updated.id ? updated : r))); setSelected(null); loadStats(); }}
        />
      )}
    </div>
  );
}
